import { useEffect, useState } from "react";
import { UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { Section, Field } from "@/components/common";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

/** Changes to apply: only fields whose new value differs from the current one. */
export const accountChanges = (cur, f) => [["email", "Admin email"], ["name", "Display name"]]
  .filter(([k]) => (k === "email" ? f.email.trim().toLowerCase() : f.name.trim()) !== cur[k])
  .map(([k, label]) => ({ k, label, old: cur[k], next: k === "email" ? f.email.trim().toLowerCase() : f.name.trim() }));

function ConfirmDialog({ tenant, changes, onClose, onConfirm }) {
  const [ok, setOk] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-account-confirm-dialog">
        <DialogHeader><DialogTitle>Confirm account details change</DialogTitle>
          <DialogDescription>Workspace: <strong data-testid="confirm-tenant-name">{tenant.name}</strong>. The admin will be notified{changes.some((c) => c.k === "email") ? " at both the old and new email" : " at their current email"}.</DialogDescription></DialogHeader>
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th>Field</th><th>Current</th><th>New</th></tr></thead>
          <tbody>{changes.map((c) => <tr key={c.k} data-testid={`confirm-row-${c.k}`}><td className="py-1 pr-3 font-medium">{c.label}</td><td className="pr-3 text-slate-500" data-testid={`confirm-old-${c.k}`}>{c.old}</td><td data-testid={`confirm-new-${c.k}`}>{c.next}</td></tr>)}</tbody></table>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" data-testid="admin-account-confirm-check" checked={ok} onChange={(e) => setOk(e.target.checked)} />
          I confirm this change was requested by the workspace and I have verified the request.</label>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="admin-account-cancel">Cancel</Button>
          <Button disabled={!ok} onClick={onConfirm} className="bg-[#13294B] hover:bg-[#1c3a66]" data-testid="admin-account-confirm">Confirm and save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminAccountCard({ tenant }) {
  const [cur, reload] = useLoad(`/platform/tenants/${tenant.id}/admin-account`);
  const [f, setF] = useState(null);
  const [review, setReview] = useState(null);
  useEffect(() => { if (cur) setF({ email: cur.email, name: cur.name }); }, [cur]);
  if (!cur || !f) return null;
  const changes = accountChanges(cur, f);
  const err = f.email.trim() && !EMAIL_RE.test(f.email.trim()) ? "Enter a valid email address, e.g. name@company.com" : !f.name.trim() ? "Display name can't be blank" : "";
  const save = async () => {
    const body = Object.fromEntries(changes.map((c) => [c.k, c.next]));
    const r = await act(() => api.put(`/platform/tenants/${tenant.id}/admin-account`, body), "Admin account updated and notifications sent");
    setReview(null);
    if (r) reload();
  };
  return (
    <Section title="Tenant admin account" testId="admin-account-card">
      <div className="grid gap-3 p-5 sm:grid-cols-3">
        <Field label="Admin email (sign-in)"><Input data-testid="admin-account-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Display name"><Input data-testid="admin-account-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <div className="flex items-end"><Button data-testid="admin-account-review" disabled={!changes.length || !!err} onClick={() => setReview(changes)} className="bg-[#13294B] hover:bg-[#1c3a66]"><UserCog className="mr-1 h-4 w-4" />Review change</Button></div>
        {err && <p className="text-xs font-medium text-rose-700 sm:col-span-3" data-testid="admin-account-error">{err}</p>}
        <p className="text-xs text-slate-500 sm:col-span-3">Sensitive action: recorded in the platform audit log. Tenant admins can't change these themselves; they request it through a support ticket.</p>
      </div>
      {review && <ConfirmDialog tenant={tenant} changes={review} onClose={() => setReview(null)} onConfirm={save} />}
    </Section>
  );
}
